export const gameSystems = {
  dnd5e: {
    id: 'dnd5e',
    name: "D&D 5e",
    currencies: [
      { id: "cp", name: "Copper", nameIt: "Rame", valueInBase: 1, colorKey: "copper", symbol: "CP", symbolIt: "MR" },
      { id: "sp", name: "Silver", nameIt: "Argento", valueInBase: 10, colorKey: "silver", symbol: "SP", symbolIt: "MA" },
      { id: "ep", name: "Electrum", nameIt: "Electrum", valueInBase: 50, colorKey: "electrum", symbol: "EP", symbolIt: "ME" },
      { id: "gp", name: "Gold", nameIt: "Oro", valueInBase: 100, colorKey: "gold", symbol: "GP", symbolIt: "MO" },
      { id: "pp", name: "Platinum", nameIt: "Platino", valueInBase: 1000, colorKey: "platinum", symbol: "PP", symbolIt: "MP" }
    ],
    baseUnit: "cp",
    defaultCurrency: "gp"
  },
  dnd35: {
    id: "dnd35",
    name: "D&D 3.5",
    currencies: [
      { id: "cp", name: "Copper piece", nameIt: "Pezzo di rame", valueInBase: 1, colorKey: "copper", symbol: "CP", symbolIt: "MR" },
      { id: "sp", name: "Silver piece", nameIt: "Pezzo d'argento", valueInBase: 10, colorKey: "silver", symbol: "SP", symbolIt: "MA" },
      { id: "gp", name: "Gold piece", nameIt: "Pezzo d'oro", valueInBase: 100, colorKey: "gold", symbol: "GP", symbolIt: "MO" },
      { id: "pp", name: "Platinum piece", nameIt: "Pezzo di platino", valueInBase: 1000, colorKey: "platinum", symbol: "PP", symbolIt: "MP" }
    ],
    baseUnit: "cp",
    defaultCurrency: "gp"
  },
  pf2e: {
    id: 'pf2e',
    name: "Pathfinder 2e",
    currencies: [
      { id: "cp", name: "Copper", nameIt: "Rame", valueInBase: 1, colorKey: "copper", symbol: "CP", symbolIt: "MR" },
      { id: "sp", name: "Silver", nameIt: "Argento", valueInBase: 10, colorKey: "silver", symbol: "SP", symbolIt: "MA" },
      { id: "gp", name: "Gold", nameIt: "Oro", valueInBase: 100, colorKey: "gold", symbol: "GP", symbolIt: "MO" },
      { id: "pp", name: "Platinum", nameIt: "Platino", valueInBase: 1000, colorKey: "platinum", symbol: "PP", symbolIt: "MP" }
    ],
    baseUnit: "cp",
    defaultCurrency: "gp"
  },
  wfrp4e: {
    id: 'wfrp4e',
    name: "Warhammer Fantasy 4e",
    currencies: [
      { id: "d", name: "Brass Pennies", nameIt: "Penny di Ottone", valueInBase: 1, colorKey: "brass", symbol: "BP", symbolIt: "PO" },
      { id: "s", name: "Silver Shillings", nameIt: "Scellini d'Argento", valueInBase: 12, colorKey: "silver", symbol: "SS", symbolIt: "SA" },
      { id: "gc", name: "Gold Crowns", nameIt: "Corone d'Oro", valueInBase: 240, colorKey: "gold", symbol: "GC", symbolIt: "CO" }
    ],
    baseUnit: "d",
    defaultCurrency: "gc"
  }
};

// Helper to get symbol based on language
export const getSymbol = (currency, lang) => {
  return lang === 'it' ? currency.symbolIt : currency.symbol;
};

// Convert any amount to base units
export const toBaseUnits = (amount, currencyId, systemId) => {
  const system = gameSystems[systemId];
  const currency = system.currencies.find(c => c.id === currencyId);
  return amount * currency.valueInBase;
};

// Convert base units to a specific currency
export const fromBaseUnits = (baseAmount, currencyId, systemId) => {
  const system = gameSystems[systemId];
  const currency = system.currencies.find(c => c.id === currencyId);
  return baseAmount / currency.valueInBase;
};

// Get total wallet value in base units
export const getTotalInBaseUnits = (wallet, systemId) => {
  const system = gameSystems[systemId];
  return system.currencies.reduce((total, currency) => {
    return total + (wallet[currency.id] || 0) * currency.valueInBase;
  }, 0);
};

// Format total value in the default currency for display
export const formatTotalValue = (wallet, systemId, lang = 'en') => {
  const system = gameSystems[systemId];
  const totalBase = getTotalInBaseUnits(wallet, systemId);
  const defaultCurrency = system.currencies.find(c => c.id === system.defaultCurrency);
  const value = totalBase / defaultCurrency.valueInBase;
  const name = lang === 'it' ? defaultCurrency.nameIt : defaultCurrency.name;
  return `${value.toFixed(2)} ${name}`;
};

// Auto-convert wallet to minimize coins
export const optimizeWallet = (wallet, systemId) => {
  const system = gameSystems[systemId];
  const totalBase = getTotalInBaseUnits(wallet, systemId);
  
  const newWallet = {};
  let remaining = totalBase;
  
  // Sort currencies by value descending
  const sortedCurrencies = [...system.currencies].sort((a, b) => b.valueInBase - a.valueInBase);
  
  for (const currency of sortedCurrencies) {
    const count = Math.floor(remaining / currency.valueInBase);
    newWallet[currency.id] = count;
    remaining -= count * currency.valueInBase;
  }
  
  return newWallet;
};

// Spend coins with auto-conversion
export const spendCoins = (wallet, amount, currencyId, systemId) => {
  const system = gameSystems[systemId];
  const currency = system.currencies.find(c => c.id === currencyId);
  const amountInBase = amount * currency.valueInBase;
  const totalBase = getTotalInBaseUnits(wallet, systemId);
  
  if (amountInBase > totalBase) {
    return { success: false, wallet, message: 'insufficientFunds' };
  }
  
  const newTotalBase = totalBase - amountInBase;
  const newWallet = {};
  let remaining = newTotalBase;
  
  // Sort currencies by value descending
  const sortedCurrencies = [...system.currencies].sort((a, b) => b.valueInBase - a.valueInBase);
  
  for (const curr of sortedCurrencies) {
    const count = Math.floor(remaining / curr.valueInBase);
    newWallet[curr.id] = count;
    remaining -= count * curr.valueInBase;
  }
  
  return { success: true, wallet: newWallet };
};

// Add coins to wallet
export const addCoins = (wallet, amount, currencyId) => {
  const newWallet = { ...wallet };
  newWallet[currencyId] = (newWallet[currencyId] || 0) + amount;
  return newWallet;
};

// Initialize empty wallet for a system
export const createEmptyWallet = (systemId) => {
  const system = gameSystems[systemId];
  const wallet = {};
  system.currencies.forEach(currency => {
    wallet[currency.id] = 0;
  });
  return wallet;
};
